// Client-side PostgreSQL schema cache, keyed by connection and scoped to one
// database.
//
// TIERED LOAD (SLICES.md § S7 items 1-4, 7-8 — the MSSQL design of
// `Docs/IntelliSenseSnapshotLoad.md` applied to pg):
//
//   phase 1 — the relation catalog (`pg_class` scan: tables, views, matviews
//             WITH their columns, sequences) plus `search_path` and the
//             `version()` probe. Published to the schema bus the moment it
//             lands, which flips the IntelliSense pill to ready and makes
//             relation-name completion work. `ensurePgSchema` resolves here.
//   phase 2 — enum labels, columns, FK edges, routines, user types,
//             extensions and (PG19+) property graphs, fetched afterwards,
//             merged page by page into the SAME snapshot object and
//             re-published as they arrive (`whenPgSnapshotComplete` awaits it).
//
// Every phase-2 fetch is paged (`introspectionPaging.ts`, 5,000 rows per
// page), gated to 2 concurrent queries per connection
// (`introspectionSemaphore.ts`) so a user query issued during the load is not
// stuck behind the catalog scans, and merged incrementally
// (`pgSnapshotMerge.ts`) with an event-loop yield between pages so no
// main-thread task exceeds 50 ms.

import type { FkEdge, PropertyGraphMeta } from '@/core/engines/intellisenseTypes';
import type { RoutineMetadata } from '@/types/electron';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { projectPgSnapshot } from '@/features/intellisense/schemaBus/adapters/pgsqlAdapter';

import { INTROSPECTION_PAGE_ROWS, yieldToEventLoop } from './introspectionPaging';
import {
  fetchPgColumnPages,
  fetchPgEnumTypes,
  fetchPgExtensions,
  fetchPgForeignKeyPages,
  fetchPgPropertyGraphs,
  fetchPgRelationCatalogPages,
  fetchPgRoutinePages,
  fetchPgSearchPath,
  fetchPgServerVersion,
  fetchPgUserTypePages,
  type PgColumnIntrospection,
  type PgRelationCatalog,
} from './pgSchemaIntrospection';
import type { PgSchemaSnapshot, PgSchemaTableEntry } from './pgSchemaTypes';
import {
  applyPgColumnPage,
  applyPgForeignKeyPage,
  applyPgRoutinePage,
  applyPgUserTypePage,
  carryOverPgColumns,
  createPgLiveSnapshot,
  pgTableKey,
  setPgExtensions,
  setPgPropertyGraphs,
  type PgLiveSnapshot,
} from './pgSnapshotMerge';
import { pgTableKeyParts } from './pgRelationKey';
import { beginIntrospectionLoad } from './introspectionTruncation';
import { SNAPSHOT_STALE_AFTER_MS } from './snapshotLifetime';
import {
  createLoadFailures,
  createTieredCacheEntry,
  isEntryFresh,
  markLoadOutcome,
  publishDegraded,
  publishThrottled,
  recordLoadFailure,
  reportPartialSnapshot,
  whenEntryColumnsLoaded,
  whenEntryComplete,
  type LoadFailures,
  type TieredCacheEntry,
} from './tieredSnapshotCache';

type CacheEntry = TieredCacheEntry<PgSchemaSnapshot>;

const cacheByConnectionId = new Map<string, CacheEntry>();
// S7 item 3 — the snapshot is built once per connection and rebuilt only on
// DDL invalidation, explicit `forceRefresh`, or reconnect. This is a
// day-scale backstop, NOT a refresh cadence (was 30_000, which re-ran the
// whole introspection fan-out while the user was editing).
const DEFAULT_TTL_MS = SNAPSHOT_STALE_AFTER_MS;


function entryFor(connectionId: string): CacheEntry {
  const existing = cacheByConnectionId.get(connectionId);
  if (existing) return existing;
  const created = createTieredCacheEntry<PgSchemaSnapshot>();
  cacheByConnectionId.set(connectionId, created);
  return created;
}

export function getCachedPgSchema(connectionId: string): PgSchemaSnapshot | undefined {
  return cacheByConnectionId.get(connectionId)?.snapshot;
}

export function invalidatePgSchema(connectionId: string): void {
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  // Bump the generation so any refresh already in flight cannot publish its
  // pre-invalidation snapshot once it resolves — and so an in-flight phase 2
  // stops merging AND stops paging.
  entry.generation += 1;
  entry.refreshPromise = undefined;
  // Soft invalidation: keep serving the last snapshot but mark it stale so the
  // next ensure() bypasses the TTL fast-path and refreshes.
  if (entry.snapshot) entry.snapshot = { ...entry.snapshot, builtAt: 0 };
}

export function dropPgSchema(connectionId: string): void {
  // Mutate the entry in place (rather than deleting the map key) so a refresh
  // whose closure still holds this entry object observes the bumped generation
  // and skips its stale publish/cache. Bumping generation is what makes the
  // drop win the race against an in-flight refresh.
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  entry.generation += 1;
  entry.snapshot = undefined;
  entry.refreshPromise = undefined;
  entry.phase2 = undefined;
  entry.columnsLoaded = undefined;
}

/** Inject a schema snapshot for testing (no introspection needed). */
export function injectPgSchemaForTest(connectionId: string, snapshot: PgSchemaSnapshot): void {
  const entry = entryFor(connectionId);
  entry.snapshot = snapshot;
  entry.lastError = undefined;
}

export function __resetPgSchemaCacheForTest(): void {
  cacheByConnectionId.clear();
}

/**
 * Phase 1 — the relation catalog plus the `search_path` / `version()` probes.
 * Publishes the moment it lands and kicks phase 2 without awaiting it.
 */
async function runPgPhase1(
  entry: CacheEntry,
  options: { connectionId: string; database: string },
): Promise<PgSchemaSnapshot> {
  const { connectionId, database } = options;
  const generation = entry.generation + 1;
  entry.generation = generation;
  const failures = createLoadFailures();
  // The record describes THIS load, not the previous one (R3).
  beginIntrospectionLoad(connectionId, database);

  const kinds = new Map<string, NonNullable<PgSchemaTableEntry['kind']>>();
  const names = new Map<string, { schema: string; name: string }>();
  const matviews: PgSchemaTableEntry[] = [];
  const sequences: PgRelationCatalog['sequences'][number][] = [];
  const relationsFailed = await fetchPgRelationCatalogPages(
    connectionId,
    database,
    (pages) => {
      for (const page of pages) {
        for (const [key, kind] of page.kinds) kinds.set(key, kind);
        for (const [key, spelled] of page.names ?? []) names.set(key, spelled);
        matviews.push(...page.matviews);
        sequences.push(...page.sequences);
      }
    },
    { shouldAbort: () => entry.generation !== generation },
  ).then(
    () => false,
    (error: unknown) => {
      recordLoadFailure(failures, 'relations', error);
      return true;
    },
  );

  const [probedSearchPath, serverVersion] = await Promise.all([
    fetchPgSearchPath(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'searchpath', error);
      return null;
    }),
    fetchPgServerVersion(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'serverinfo', error);
      return '';
    }),
  ]);

  // The relation catalog is the ONLY source of the relation list now: the
  // column scan can no longer introduce a relation, because it lands in phase
  // 2 and would otherwise make the published table list grow under the user.
  // Matviews already carry their `pg_attribute` columns; everything else
  // starts empty and is filled in by phase 2.
  //
  // Keys are case-exact (`pgTableKey`); each entry carries the relation's
  // CATALOG spelling (`names`), so a mixed-case `"MidVqeMixed"` reaches every
  // consumer — and every SQL emitter — as the server spells it, not as
  // `midvqemixed`, and a sibling `midvqemixed` stays a relation of its own.
  const fresh: PgSchemaTableEntry[] = [];
  const matviewByKey = new Map<string, PgSchemaTableEntry>();
  for (const mv of matviews) matviewByKey.set(pgTableKey(mv.schema, mv.name), mv);
  for (const [key, kind] of kinds) {
    const mv = matviewByKey.get(key);
    if (mv) {
      fresh.push(mv);
      continue;
    }
    const [keySchema, keyName] = pgTableKeyParts(key);
    const spelled = names.get(key);
    fresh.push({
      schema: spelled?.schema ?? keySchema,
      name: spelled?.name ?? keyName,
      columns: [],
      kind,
    });
  }

  // Stale-while-revalidate carries the previous refresh's payload forward —
  // but only within the SAME database. The cache is keyed by connection and a
  // database switch lands here with the other database's snapshot in `entry`;
  // matching its same-named tables would hand database B the columns of A's
  // tables until phase 2 replaces them (release review 1.5.0, F4).
  const previous = entry.snapshot?.database === database ? entry.snapshot : undefined;
  const snapshot: PgSchemaSnapshot = {
    connectionId,
    database,
    builtAt: Date.now(),
    // Stale-while-revalidate: keep the previous refresh's columns on relations
    // that still exist until phase 2 replaces them, relation by relation.
    tables: carryOverPgColumns(previous?.tables, fresh),
    columnsByTableLower: {},
    routinesByNameLower: previous?.routinesByNameLower ?? {},
    searchPath: probedSearchPath ?? [],
    searchPathKnown: probedSearchPath !== null,
    foreignKeys: previous?.foreignKeys ?? [],
    extensions: previous?.extensions ?? [],
    serverVersion,
    userTypes: previous?.userTypes ?? [],
    sequences,
    propertyGraphs: previous?.propertyGraphs ?? [],
    phase: 'tables',
  };
  for (const table of snapshot.tables) {
    (snapshot.columnsByTableLower[table.name.toLowerCase()] ??= []).push(...table.columns);
  }

  if (relationsFailed) {
    return publishDegraded(entry, generation, {
      previous,
      built: snapshot,
      failures,
      engine: 'pgsql',
      project: projectPgSnapshot,
    });
  }

  const live = await createPgLiveSnapshot(snapshot);
  // A drop / newer refresh landed while we were projecting — discard.
  if (entry.generation !== generation) return snapshot;

  entry.snapshot = snapshot;
  entry.lastError = undefined;
  publishThrottled(entry, live.projected, true);
  entry.phase2 = runPgPhase2(entry, live, generation, failures);
  return snapshot;
}

/**
 * Phase 2 — columns, FK edges, routines, user types, extensions and property
 * graphs, paged, merged incrementally and re-published as pages land. Never
 * rejects: an artefact that fails degrades to whatever pages already merged
 * and is named in the `feature:schema_snapshot_partial` event.
 */
async function runPgPhase2(
  entry: CacheEntry,
  live: PgLiveSnapshot,
  generation: number,
  failures: LoadFailures,
): Promise<void> {
  const { connectionId, database } = live.native;
  const superseded = (): boolean => entry.generation !== generation;

  const onPage = (merge: () => void): void => {
    if (superseded()) return;
    merge();
    publishThrottled(entry, live.projected, false);
  };

  const step = async (name: string, run: () => Promise<void>): Promise<void> => {
    if (superseded()) return;
    try {
      await run();
    } catch (error) {
      recordLoadFailure(failures, name, error);
    }
    if (!superseded()) publishThrottled(entry, live.projected, false);
  };

  const abort = { shouldAbort: superseded };
  // Enum labels first, then columns: a column's `enumValues` come from the
  // `pg_enum` map, so the map has to exist before the first column page is
  // aggregated. Both are inside ONE step so the pair is atomic. Exposed as
  // `columnsLoaded` (set before this function first yields) so the Visual
  // Query Editor can start editing without waiting for the other steps.
  const columns = step('columns', async () => {
    const enumTypes = await fetchPgEnumTypes(connectionId, database, abort);
    if (superseded()) return;
    await fetchPgColumnPages(
      connectionId,
      database,
      enumTypes,
      (page: readonly PgColumnIntrospection[]) => onPage(() => applyPgColumnPage(live, page)),
      abort,
    );
  });
  entry.columnsLoaded = columns;
  await Promise.all([
    columns,
    step('fk', () =>
      fetchPgForeignKeyPages(
        connectionId,
        database,
        (page: readonly FkEdge[]) => onPage(() => applyPgForeignKeyPage(live, page)),
        abort,
      ),
    ),
    step('routines', () =>
      fetchPgRoutinePages(
        connectionId,
        database,
        (page: readonly RoutineMetadata[]) => onPage(() => applyPgRoutinePage(live, page)),
        abort,
      ),
    ),
    step('types', () =>
      fetchPgUserTypePages(
        connectionId,
        database,
        (page) => onPage(() => applyPgUserTypePage(live, page)),
        abort,
      ),
    ),
    step('extensions', async () => {
      const extensions = await fetchPgExtensions(connectionId, database);
      onPage(() => setPgExtensions(live, extensions));
    }),
    step('graphs', async () => {
      const graphs: PropertyGraphMeta[] = await fetchPgPropertyGraphs(
        connectionId,
        database,
        live.native.serverVersion ?? '',
      );
      onPage(() => setPgPropertyGraphs(live, graphs));
    }),
  ]);

  if (superseded()) return;
  live.native.phase = 'complete';
  markLoadOutcome(entry, failures.missing, { connectionId, database });
  publishThrottled(entry, live.projected, true);
  reportPartialSnapshot('pgsql', failures);
}

export async function ensurePgSchema(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<PgSchemaSnapshot> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const entry = entryFor(options.connectionId);

  // The cache is keyed by connection, but a snapshot describes ONE database.
  // With the day-scale TTL a database switch would otherwise keep serving the
  // previous database's objects forever, so a scope mismatch counts as a miss.
  const scopeMatches = entry.snapshot?.database === options.database;

  if (
    !options.forceRefresh
    && entry.snapshot
    && scopeMatches
    && isEntryFresh(entry, ttlMs)
  ) {
    // The bus is cleared independently of this cache — by the DDL invalidator
    // and by disconnect/reconnect (`invalidateConnection`). The snapshot no
    // longer expires inside a session, so nothing else would ever re-publish
    // it: re-publish here whenever the bus lost the entry, or the resolver
    // would read a cold bus forever.
    if (!getSchemaBusSnapshot(options.connectionId, options.database)) {
      publishThrottled(entry, projectPgSnapshot(entry.snapshot), true);
    }
    return entry.snapshot;
  }

  // Dedup only a refresh of the SAME database: handing a request for
  // database B the in-flight promise for A resolves B's caller with A's
  // snapshot (release review 1.5.0, F4). Starting B's refresh bumps the
  // generation, so A's late publish is discarded by the guards.
  if (entry.refreshPromise && entry.refreshScope === options.database) {
    return entry.refreshPromise;
  }

  const refresh = runPgPhase1(entry, options)
    .catch((error: unknown) => {
      entry.lastError =
        error instanceof Error ? error.message : 'Failed to load PostgreSQL schema';
      throw error;
    })
    .finally(() => {
      // Only clear OUR in-flight marker: a drop/invalidate may have handed
      // `refreshPromise` to a newer refresh while we were in flight.
      if (entry.refreshPromise === refresh) {
        entry.refreshPromise = undefined;
        entry.refreshScope = undefined;
      }
    });
  entry.refreshPromise = refresh;
  entry.refreshScope = options.database;
  return refresh;
}

/**
 * Resolve once the snapshot for `connectionId` is fully loaded — phase 1
 * published AND phase 2 finished merging. Returns immediately when nothing is
 * in flight. Never rejects — a failed refresh resolves, because "complete"
 * here means "no longer loading", not "succeeded".
 */
export async function whenPgSnapshotComplete(connectionId: string): Promise<void> {
  await whenEntryComplete(cacheByConnectionId.get(connectionId));
}

/**
 * Resolve once the snapshot's columns have loaded (phase 1 + the phase-2
 * column step), without waiting for FK edges, routines, types, extensions or
 * property graphs — see `whenEntryColumnsLoaded`.
 */
export async function whenPgColumnsLoaded(connectionId: string): Promise<void> {
  await whenEntryColumnsLoaded(cacheByConnectionId.get(connectionId));
}

/** `ensurePgSchema` + `whenPgColumnsLoaded` — the Visual Query Editor's live
 *  schema pull: editable once tables + columns are in; the later artefacts
 *  arrive through the schema bus. */
export async function ensurePgSchemaWithColumns(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<PgSchemaSnapshot> {
  const snapshot = await ensurePgSchema(options);
  await whenPgColumnsLoaded(options.connectionId);
  return getCachedPgSchema(options.connectionId) ?? snapshot;
}

/** `ensurePgSchema` + `whenPgSnapshotComplete` — for callers that need the
 *  WHOLE snapshot (the coverage harness's post-DDL hook, the pill's "Reload
 *  schema" action). */
export async function ensurePgSchemaComplete(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<PgSchemaSnapshot> {
  const snapshot = await ensurePgSchema(options);
  await whenPgSnapshotComplete(options.connectionId);
  return getCachedPgSchema(options.connectionId) ?? snapshot;
}

/**
 * Test seam for the long-task perf gate: build a synthetic catalog of
 * `tableCount` relations x `columnsPerTable` columns and push it through the
 * REAL merge path — the same chunked `createPgLiveSnapshot` projection, the
 * same `applyPgColumnPage` merge in `INTROSPECTION_PAGE_ROWS`-sized pages, the
 * same throttled bus publishes. Rows are generated per page rather than up
 * front so the generation itself cannot be the long task the gate measures.
 */
export async function injectSyntheticPgSnapshotForTest(options: {
  connectionId: string;
  database: string;
  tableCount: number;
  columnsPerTable: number;
}): Promise<void> {
  const entry = entryFor(options.connectionId);
  const generation = entry.generation + 1;
  entry.generation = generation;

  const tables: PgSchemaTableEntry[] = [];
  for (let i = 0; i < options.tableCount; i += 1) {
    tables.push({ schema: 'public', name: `synthetic_table_${i}`, columns: [], kind: 'table' });
    if (tables.length % INTROSPECTION_PAGE_ROWS === 0) await yieldToEventLoop();
  }

  const snapshot: PgSchemaSnapshot = {
    connectionId: options.connectionId,
    database: options.database,
    builtAt: Date.now(),
    tables,
    columnsByTableLower: {},
    routinesByNameLower: {},
    searchPath: ['public'],
    foreignKeys: [],
    extensions: [],
    serverVersion: '',
    userTypes: [],
    sequences: [],
    propertyGraphs: [],
    phase: 'tables',
  };
  const live = await createPgLiveSnapshot(snapshot);
  entry.snapshot = snapshot;
  publishThrottled(entry, live.projected, true);

  let page: PgColumnIntrospection[] = [];
  const flush = async (): Promise<void> => {
    if (page.length === 0) return;
    applyPgColumnPage(live, page);
    page = [];
    publishThrottled(entry, live.projected, false);
    await yieldToEventLoop();
  };
  for (let t = 0; t < options.tableCount; t += 1) {
    for (let c = 0; c < options.columnsPerTable; c += 1) {
      page.push({
        schema: 'public',
        table: `synthetic_table_${t}`,
        column: { name: `col_${c}`, dataType: 'text', isPrimaryKey: c === 0 },
      });
      if (page.length >= INTROSPECTION_PAGE_ROWS) await flush();
    }
  }
  await flush();
  live.native.phase = 'complete';
  publishThrottled(entry, live.projected, true);
}
